"use client";

import { useCallback, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { IoCloseOutline } from "react-icons/io5";
import type { CategoryItem, CategoryPanelProps } from "../../../types/category-panel";
import { CategoryDepartmentList } from "../CategoryDepartmentList";
import { CategoryDetailCard } from "../CategoryDetailCard";

/** Só abre o popover se houver algo pra mostrar (subcategorias, marcas, banner ou vitrine). */
function hasDetail(cat: CategoryItem): boolean {
    return Boolean(
        cat.children?.length ||
            cat.brands?.length ||
            cat.panelProductIds?.length ||
            cat.panelVideoDesktopUrl ||
            cat.panelVideoMobileUrl ||
            cat.panelImageDesktopUrl ||
            cat.bannerUrl ||
            cat.imageUrl ||
            cat.panelImageMobileUrl
    );
}

/**
 * Drawer pela esquerda com a lista de departamentos + popover de detalhes
 * (CategoryDetailCard) ao hover, grudado à direita e alinhado ao eixo Y do item.
 *
 * É sempre a versão desktop — quem decide se deve estar visível é o pai
 * (CategoryPanelModern por `useIsMobile`, CategoryPanelResponsive por CSS).
 */
export function CategoryPanelDesktopDrawer({
    categories = [],
    onClose,
    title = "Departamentos",
}: CategoryPanelProps) {
    const [activeId, setActiveId] = useState<string | null>(null);
    const [popoverTop, setPopoverTop] = useState(0);
    /**
     * Um clique DENTRO do popover (drill-down de subcategoria) fixa o card.
     * Sem isso: entrar num subnível muda a altura do popover e, se o ponteiro
     * estiver perto da borda, ele deixa de estar sobre o elemento — o
     * `mouseleave` do wrapper dispara e o painel fecha no meio da navegação.
     * Enquanto fixado, o hover não fecha nada; voltar o mouse pro drawer
     * (`onMouseEnter` do aside) devolve o comportamento normal de hover.
     */
    const [pinned, setPinned] = useState(false);

    // Guarda também o topo do botão, pra alinhar o popover na mesma altura.
    const activate = useCallback((id: string, e: ReactMouseEvent<HTMLButtonElement>) => {
        setActiveId(id);
        setPopoverTop(e.currentTarget.getBoundingClientRect().top);
    }, []);

    if (categories.length === 0) return null;

    const active = categories.find((c) => c.id === activeId);

    return (
        <>
            <div
                className="fixed inset-0 z-40 animate-drawer-fade-in bg-black/50"
                aria-hidden="true"
                onClick={onClose}
            />

            {/* Wrapper — drawer e popover compartilham o hover */}
            <div
                className="pointer-events-none fixed left-0 top-0 z-50 h-[100dvh] [&>*]:pointer-events-auto"
                onMouseLeave={() => {
                    if (!pinned) setActiveId(null);
                }}
            >
                <aside
                    className="fixed left-0 top-0 z-50 flex h-[100dvh] w-[min(320px,100vw)] animate-drawer-slide-in flex-col bg-base-100 shadow-[4px_0_24px_color-mix(in_srgb,black_15%,transparent)]"
                    aria-label={title}
                    onMouseEnter={() => setPinned(false)}
                >
                    <div className="flex h-[50px] shrink-0 items-center justify-between bg-primary px-5 text-primary-content">
                        <span className="text-sm font-bold uppercase tracking-[0.1em]">{title}</span>
                        <button
                            type="button"
                            className="flex h-8 w-8 items-center justify-center rounded-md transition-colors duration-150 hover:bg-primary-content/15"
                            aria-label="Fechar departamentos"
                            onClick={onClose}
                        >
                            <IoCloseOutline size={22} />
                        </button>
                    </div>

                    <CategoryDepartmentList
                        categories={categories}
                        activeId={activeId}
                        onActivate={activate}
                        className="flex-1 overflow-y-auto py-2"
                    />
                </aside>

                {active && hasDetail(active) && (
                    <div
                        className="fixed left-[min(320px,100vw)] z-50 max-h-[80vh] max-w-[calc(100vw-320px-1rem)] animate-popover-fade-in overflow-y-auto rounded-r-xl bg-base-100 shadow-[8px_4px_24px_color-mix(in_srgb,black_12%,transparent)]"
                        style={{ top: popoverTop }}
                        onMouseDown={() => setPinned(true)}
                    >
                        {/* `key` remonta ao trocar de departamento, resetando
                            o drill-down interno do card. */}
                        <CategoryDetailCard key={active.id} category={active} onClose={onClose} />
                    </div>
                )}
            </div>
        </>
    );
}
